import type { FormEvent } from 'react';
import type CardDb from 'src/model/CardDb';
import { FormInputNameCreditCard } from '../../../../../features/userProfile/types';
import type { UseFormReturn } from '../../../../../hooks';
import type { Address } from 'src/model/Address';

interface OptionProps {
  value?: string | number;
  label: string;
  target?: HTMLInputElement;
}

interface AddCreditCardUIProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  onSubmitRegister: (e: FormEvent) => void;
  cards: CardDb[];
  onDeleteCard: (id: string) => void;
  addresses: Address[];
  selectedAddressId: string;
  onAddressSelection: (addressId: string) => void;
  showNewAddressForm: boolean;
  newAddressData: any;
  newAddressErrors: any;
  onChangeNewAddressInput: any;
  onChangeCEP: (cep: string) => void;
}

export const CreditCardPaymentAddUI = ({
  formData,
  formErrors,
  cards,
  onChangeFormInput,
  onSubmitRegister,
  onDeleteCard,
  addresses,
  selectedAddressId,
  onAddressSelection,
  showNewAddressForm,
  newAddressData,
  newAddressErrors,
  onChangeNewAddressInput,
  onChangeCEP,
}: AddCreditCardUIProps) => {
  const stateOptions: OptionProps[] = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' },
  ];

  const addressOptions: OptionProps[] = [
    { value: '', label: 'Selecione um endereço' },
    ...addresses.map((addr) => ({
      value: addr.id,
      label: `${addr.street}, ${addr.number} - ${addr.city}/${addr.state} ${addr.main ? '(Principal)' : ''}`,
    })),
    { value: 'new', label: 'Cadastrar novo endereço' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Cartões de Crédito
          </h1>
          <p className="text-lg text-gray-600">
            Adicione e gerencie seus cartões
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 md:px-6 px-2 py-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Adicionar Novo Cartão
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Preencha os dados do seu cartão
                </p>
              </div>

              <div className="px-2 md:px-6 py-8">
                <form onSubmit={onSubmitRegister} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Número do cartão
                    </label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      value={formData[FormInputNameCreditCard.number]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputNameCreditCard.number)(
                          e.target.value
                        )
                      }
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                    />
                    {formErrors.number && (
                      <p className="text-sm text-red-600">
                        {formErrors.number[0]}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Vencimento
                      </label>
                      <input
                        type="text"
                        placeholder="MM/AA"
                        maxLength={5}
                        value={formData[FormInputNameCreditCard.date]}
                        onChange={(e) =>
                          onChangeFormInput(FormInputNameCreditCard.date)(
                            e.target.value
                          )
                        }
                        className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                      />
                      {formErrors.date && (
                        <p className="text-sm text-red-600">
                          {formErrors.date[0]}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        CVV
                      </label>
                      <input
                        type="text"
                        placeholder="000"
                        value={formData[FormInputNameCreditCard.cvv]}
                        onChange={(e) =>
                          onChangeFormInput(FormInputNameCreditCard.cvv)(
                            e.target.value
                          )
                        }
                        className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                      />
                      {formErrors.cvv && (
                        <p className="text-sm text-red-600">
                          {formErrors.cvv[0]}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Nome do titular
                    </label>
                    <input
                      type="text"
                      placeholder="Maria da Silva"
                      value={formData[FormInputNameCreditCard.name]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputNameCreditCard.name)(
                          e.target.value
                        )
                      }
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                    />
                    {formErrors.name && (
                      <p className="text-sm text-red-600">
                        {formErrors.name[0]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      CPF do titular
                    </label>
                    <input
                      type="text"
                      placeholder="123.456.789-00"
                      value={formData[FormInputNameCreditCard.document]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputNameCreditCard.document)(
                          e.target.value
                        )
                      }
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                    />
                    {formErrors.document && (
                      <p className="text-sm text-red-600">
                        {formErrors.document[0]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Nome do cartão (opcional)
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Cartão Principal"
                      value={formData['label'] || ''}
                      onChange={(e) =>
                        onChangeFormInput('label')(e.target.value)
                      }
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                    />
                    {formErrors.label && (
                      <p className="text-sm text-red-600">
                        {formErrors.label[0]}
                      </p>
                    )}
                  </div>

                  <div className="border-t border-gray-100 pt-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Endereço de Cobrança *
                      </label>
                      <select
                        value={selectedAddressId}
                        onChange={(e) => onAddressSelection(e.target.value)}
                        className="w-full h-12 px-4 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900"
                      >
                        {addressOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {showNewAddressForm && (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-red-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Novo Endereço
                      </h3>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              CEP
                            </label>
                            <input
                              type="text"
                              placeholder="00000-000"
                              value={newAddressData.zipcode}
                              onChange={(e) => {
                                onChangeNewAddressInput('zipcode')(
                                  e.target.value
                                );
                                onChangeCEP(e.target.value);
                              }}
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                            />
                            {newAddressErrors.zipcode && (
                              <p className="text-sm text-red-600">
                                {newAddressErrors.zipcode[0]}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Rua
                            </label>
                            <input
                              type="text"
                              placeholder="Nome da rua"
                              value={newAddressData.street}
                              onChange={(e) =>
                                onChangeNewAddressInput('street')(
                                  e.target.value
                                )
                              }
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                            />
                            {newAddressErrors.street && (
                              <p className="text-sm text-red-600">
                                {newAddressErrors.street[0]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Número
                            </label>
                            <input
                              type="text"
                              placeholder="123"
                              value={newAddressData.number}
                              onChange={(e) =>
                                onChangeNewAddressInput('number')(
                                  e.target.value
                                )
                              }
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                            />
                            {newAddressErrors.number && (
                              <p className="text-sm text-red-600">
                                {newAddressErrors.number[0]}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Bairro
                            </label>
                            <input
                              type="text"
                              placeholder="Nome do bairro"
                              value={newAddressData.district}
                              onChange={(e) =>
                                onChangeNewAddressInput('district')(
                                  e.target.value
                                )
                              }
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                            />
                            {newAddressErrors.district && (
                              <p className="text-sm text-red-600">
                                {newAddressErrors.district[0]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Cidade
                            </label>
                            <input
                              type="text"
                              placeholder="Nome da cidade"
                              value={newAddressData.city}
                              onChange={(e) =>
                                onChangeNewAddressInput('city')(e.target.value)
                              }
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                            />
                            {newAddressErrors.city && (
                              <p className="text-sm text-red-600">
                                {newAddressErrors.city[0]}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Estado
                            </label>
                            <select
                              value={newAddressData.state}
                              onChange={(e) =>
                                onChangeNewAddressInput('state')(e.target.value)
                              }
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900"
                            >
                              <option value="">Selecione</option>
                              {stateOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {newAddressErrors.state && (
                              <p className="text-sm text-red-600">
                                {newAddressErrors.state[0]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 items-end">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              Complemento (opcional)
                            </label>
                            <input
                              type="text"
                              placeholder="Apto 123, Bloco A"
                              value={newAddressData.complement}
                              onChange={(e) =>
                                onChangeNewAddressInput('complement')(
                                  e.target.value
                                )
                              }
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all duration-200 text-gray-900 placeholder-gray-400"
                            />
                          </div>

                          <div className="flex items-center gap-3 h-11">
                            <input
                              type="checkbox"
                              id="main"
                              name="main"
                              checked={newAddressData.main === 'true'}
                              onChange={(e) =>
                                onChangeNewAddressInput('main')(
                                  e.target.checked.toString()
                                )
                              }
                              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            />
                            <label
                              htmlFor="main"
                              className="text-sm font-medium text-gray-700"
                            >
                              Endereço principal
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={
                        formData.number === '' ||
                        formData.date === '' ||
                        formData.cvv === '' ||
                        formData.name === '' ||
                        formData.document === '' ||
                        (!selectedAddressId && !showNewAddressForm)
                      }
                      className="w-full h-12 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      Adicionar Cartão
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 md:px-5 px-2 py-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Cartões Salvos
                  </h2>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {cards.length} {cards.length === 1 ? 'cartão' : 'cartões'}
                  </span>
                </div>
              </div>

              <div className="p-2 md:p-6">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {cards.length > 0 ? (
                    cards.map((card, index) => (
                      <div
                        key={card.id}
                        className="group relative bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-5 text-white hover:from-red-600 hover:to-red-700 transition-all duration-300"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <p className="text-xs opacity-75 mb-1">
                              CARTÃO {index + 1}
                            </p>
                            <p className="font-medium text-sm truncate">
                              {card.cardholderName}
                            </p>
                          </div>
                          <button
                            onClick={() => onDeleteCard(card.id)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-white/20 rounded-lg transition-all duration-200"
                            title="Excluir cartão"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="font-mono text-sm tracking-wider">
                            {card.exibitionNumber}
                          </div>
                          <div className="text-right">
                            <p className="text-xs opacity-75">Válido até</p>
                            <p className="text-xs font-medium">
                              {card.expirationDate}
                            </p>
                          </div>
                        </div>

                        <div className="absolute top-3 right-3 w-8 h-6 bg-white/20 rounded-md flex items-center justify-center">
                          <div className="w-4 h-3 bg-white/30 rounded-sm"></div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-sm mb-1">
                        Nenhum cartão cadastrado
                      </p>
                      <p className="text-gray-400 text-xs">
                        Adicione seu primeiro cartão
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
